// @ts-strict-ignore
import { Rectangle3D } from '../../model/rectangle-3d';
import { BufferAttribute, BufferGeometry, Group, Material, Matrix4, Mesh, Object3D, Vector3 } from 'three';
import { ArrayUtils } from '@shared/util/array-utils';
import { MathUtils } from './math-utils';
import { Face3D } from '../../model/face-3d';
import { GeometryUtils } from './geometry-utils';
import { IsThreeJsType } from './is-threejs-type';

export class FaceUtils {
  public static getFaces(mesh: Mesh): Face3D[] {
    const result: Face3D[] = [];

    const vertices: Vector3[] = GeometryUtils.getVertices(mesh.geometry);
    for (const face of GeometryUtils.getFaces(mesh.geometry)) {
      const faceVertices: Vector3[] = [vertices[face.a].clone(), vertices[face.b].clone(), vertices[face.c].clone()];
      result.push(new Face3D(faceVertices));
    }

    return result;
  }

  public static findMinMaxForMesh(mesh: Mesh, minPoint: Vector3, maxPoint: Vector3, inGlobal: boolean = false,
    targetContainer: Object3D = null): void {

    const vertices = GeometryUtils.getVertices(mesh.geometry);

    for (const vertex of vertices) {
      let vertexToCheck: Vector3 = vertex.clone();
      if (inGlobal) {
        vertexToCheck = MathUtils.localToGlobal(mesh, vertex);
      } else if (targetContainer) {
        vertexToCheck = MathUtils.localToGlobal(mesh, vertex);
        const matrixWorld = targetContainer.matrixWorld.clone().invert();
        vertexToCheck.applyMatrix4(matrixWorld);
      }

      if (maxPoint.x < vertexToCheck.x) {
        maxPoint.x = vertexToCheck.x;
      }
      if (maxPoint.y < vertexToCheck.y) {
        maxPoint.y = vertexToCheck.y;
      }
      if (maxPoint.z < vertexToCheck.z) {
        maxPoint.z = vertexToCheck.z;
      }
      if (minPoint.x > vertexToCheck.x) {
        minPoint.x = vertexToCheck.x;
      }
      if (minPoint.y > vertexToCheck.y) {
        minPoint.y = vertexToCheck.y;
      }
      if (minPoint.z > vertexToCheck.z) {
        minPoint.z = vertexToCheck.z;
      }
    }
    GeometryUtils.setVertices(mesh.geometry, vertices);
  }

  public static findMinMaxForRectangle(rectangle: Rectangle3D, minPoint: Vector3, maxPoint: Vector3): void {
    this.findMinMaxForVertices(rectangle.toArray(), minPoint, maxPoint);
  }

  public static findMinMaxForVertices(vertices: Vector3[], minPoint: Vector3, maxPoint: Vector3): void {
    for (const vertexToCheck of vertices) {
      if (maxPoint.x < vertexToCheck.x) {
        maxPoint.x = vertexToCheck.x;
      }
      if (maxPoint.y < vertexToCheck.y) {
        maxPoint.y = vertexToCheck.y;
      }
      if (maxPoint.z < vertexToCheck.z) {
        maxPoint.z = vertexToCheck.z;
      }
      if (minPoint.x > vertexToCheck.x) {
        minPoint.x = vertexToCheck.x;
      }
      if (minPoint.y > vertexToCheck.y) {
        minPoint.y = vertexToCheck.y;
      }
      if (minPoint.z > vertexToCheck.z) {
        minPoint.z = vertexToCheck.z;
      }
    }
  }

  public static moveMeshByOffset(mesh: Mesh, offset: Vector3): void {
    if (mesh) {
      const vertices = GeometryUtils.getVertices(mesh.geometry);
      for (const vertex of vertices) {
        vertex.add(offset);
      }
      GeometryUtils.setVertices(mesh.geometry, vertices);
    }
  }

  public static scaleMesh(mesh: Mesh, startPoint: Vector3, scaleMatrix: Matrix4): void {
    if (!mesh || !startPoint || !scaleMatrix) {
      return;
    }
    const vertices = GeometryUtils.getVertices(mesh.geometry);
    for (const vertex of vertices) {
      vertex.sub(startPoint);
      vertex.applyMatrix4(scaleMatrix);
      vertex.add(startPoint);
    }
    GeometryUtils.setVertices(mesh.geometry, vertices);
  }

  public static reorderUVs(group: Group): void {
    if (!group || ArrayUtils.isEmpty(group.children)) {
      return;
    }
    const meshes: Mesh[] = group.children.filter((object: Object3D) => IsThreeJsType.mesh(object)) as Mesh[];
    meshes.forEach((mesh: Mesh) => this.reorderMeshUVs(mesh));
  }

  public static reorderMeshUVs(mesh: Mesh): void {
    if (mesh) {
      const geometry: BufferGeometry = mesh.geometry;
      const uvAttribute = geometry.getAttribute('uv') as BufferAttribute;
      if (uvAttribute) {
        for (let i = 0; i < uvAttribute.count; i++) {
          uvAttribute.setX(i, 1 - uvAttribute.getX(i));
        }
        uvAttribute.needsUpdate = true;
      }
    }
  }

  public static inverseUVDirection(mesh: Mesh): void {
    if (!mesh) {
      return;
    }
    const uvAttribute = (mesh.geometry).getAttribute('uv') as BufferAttribute;
    if (!uvAttribute) {
      return;
    }
    for (let i = 0; i < uvAttribute.count; i++) {
      const x = uvAttribute.getX(i);
      const y = uvAttribute.getY(i);
      uvAttribute.setX(i, 1 - y);
      uvAttribute.setY(i, 1 - x);
    }
    uvAttribute.needsUpdate = true;
  }

  public static transformMesh(mesh: Mesh, matrix: Matrix4, roundVertex: boolean = false): void {
    if (!mesh) {
      return;
    }
    const vertices = GeometryUtils.getVertices(mesh.geometry);
    for (const vertex of vertices) {
      MathUtils.transform(vertex, matrix);
      if (roundVertex) {
        vertex.x = MathUtils.roundNumberValue(vertex.x);
        vertex.y = MathUtils.roundNumberValue(vertex.y);
        vertex.z = MathUtils.roundNumberValue(vertex.z);
      }
    }
    GeometryUtils.setVertices(mesh.geometry, vertices);
  }

  public static moveMeshBetweenContainers(mesh: Mesh, containerFrom: Object3D, containerTo: Object3D): void {
    if (mesh) {
      const vertices = GeometryUtils.getVertices(mesh.geometry);
      for (const vertex of vertices) {
        MathUtils.movePointBetweenContainers(vertex, containerFrom, containerTo);
      }
      GeometryUtils.setVertices(mesh.geometry, vertices);
    }
  }

  /**
   * Rotates mesh by it's center.
   * @param mesh
   * @param axis
   * @param angle angle in degrees
   */
  public static rotateMesh(mesh: Mesh, axis: Vector3, angle: number): void {
    if (!mesh || !axis || !angle) {
      return;
    }
    mesh.geometry.computeBoundingBox();
    const targetPosition: Vector3 = mesh.geometry.boundingBox.getCenter(new Vector3());
    mesh.localToWorld(targetPosition);
    mesh.rotateOnAxis(axis, MathUtils.toRadians(angle));
    const sourcePosition: Vector3 = mesh.geometry.boundingBox.getCenter(new Vector3());
    mesh.updateMatrixWorld(true);
    mesh.localToWorld(sourcePosition);
    const offset: Vector3 = targetPosition.clone().sub(sourcePosition);
    mesh.worldToLocal(offset);
    FaceUtils.moveMeshByOffset(mesh, offset);
  }

  public static createSingleFaceMesh(v1: Vector3, v2: Vector3, v3: Vector3, material: Material): Mesh {
    const geometry = new BufferGeometry();
    const vertices = new Float32Array([
      v1.x, v1.y, v1.z,
      v2.x, v2.y, v2.z,
      v3.x, v3.y, v3.z
    ]);

    geometry.setAttribute('position', new BufferAttribute(vertices, 3));

    const uvs = new Float32Array([
      1, 1,
      0, 0,
      1, 0
    ]);
    geometry.setAttribute('uv', new BufferAttribute(uvs, 2));

    const indices = new Uint16Array([0, 2, 1]);
    geometry.setIndex(new BufferAttribute(indices, 1));

    geometry.computeVertexNormals();

    const mesh: Mesh = new Mesh(geometry, material);
    return mesh;
  }
}
